import type {
  EvaluationResult,
  ExerciseItem,
  UserAnswer,
  ExerciseMode,
} from '../../types/exercise';

/**
 * Normalize a string for comparison:
 * - Convert to lowercase
 * - Trim whitespace
 * - Optionally strip diacritics
 */
export function normalizeAnswer(text: string, stripDiacritics = false): string {
  let normalized = text.trim().toLowerCase();

  if (stripDiacritics) {
    normalized = removeDiacritics(normalized);
  }

  // Normalize multiple spaces to single space
  normalized = normalized.replace(/\s+/g, ' ');

  return normalized;
}

/**
 * Remove diacritical marks from a string
 * e.g., "café" -> "cafe", "naïve" -> "naive"
 */
export function removeDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Calculate Levenshtein distance between two strings
 * Used for typo tolerance
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;

  // Create a 2D array to store distances
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  // Initialize base cases
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  // Fill the matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],     // deletion
          dp[i][j - 1],     // insertion
          dp[i - 1][j - 1]  // substitution
        );
      }
    }
  }

  return dp[m][n];
}

/**
 * Calculate similarity score between two strings (0-1)
 * 1 = identical, 0 = completely different
 */
export function calculateSimilarity(str1: string, str2: string): number {
  if (str1 === str2) return 1;
  if (str1.length === 0 || str2.length === 0) return 0;

  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  return 1 - distance / maxLength;
}

/**
 * Check if a typo is within acceptable tolerance
 * @param similarity - Similarity score (0-1)
 * @param tolerance - Tolerance threshold (default 0.2 = 20% difference allowed)
 */
export function isWithinTypoTolerance(
  similarity: number,
  tolerance: number = 0.2
): boolean {
  return similarity >= (1 - tolerance);
}

/**
 * Evaluate a TYPE_IN answer
 */
function evaluateTypeIn(
  userText: string,
  correctAnswer: string,
  typoTolerance: number
): EvaluationResult {
  const normalizedUser = normalizeAnswer(userText);
  const normalizedCorrect = normalizeAnswer(correctAnswer);

  // Check for exact match (after normalization)
  if (normalizedUser === normalizedCorrect) {
    return {
      isCorrect: true,
      similarityScore: 1,
      correctAnswer,
      normalizedUserAnswer: normalizedUser,
      feedback: 'Správně!',
      typoDetected: false,
    };
  }

  // Check with diacritics stripped
  const userNoDiacritics = normalizeAnswer(userText, true);
  const correctNoDiacritics = normalizeAnswer(correctAnswer, true);

  if (userNoDiacritics === correctNoDiacritics) {
    return {
      isCorrect: true,
      similarityScore: 0.95,
      correctAnswer,
      normalizedUserAnswer: normalizedUser,
      feedback: 'Správně! (drobná odchylka v diakritice)',
      typoDetected: true,
      suggestedCorrection: correctAnswer,
    };
  }

  // Check for typo tolerance
  const similarity = calculateSimilarity(normalizedUser, normalizedCorrect);

  if (isWithinTypoTolerance(similarity, typoTolerance)) {
    return {
      isCorrect: true,
      similarityScore: similarity,
      correctAnswer,
      normalizedUserAnswer: normalizedUser,
      feedback: `Správně! (překlep: "${userText}" → "${correctAnswer}")`,
      typoDetected: true,
      suggestedCorrection: correctAnswer,
    };
  }

  // Incorrect answer
  return {
    isCorrect: false,
    similarityScore: similarity,
    correctAnswer,
    normalizedUserAnswer: normalizedUser,
    feedback: `Špatně. Správná odpověď: ${correctAnswer}`,
    typoDetected: false,
  };
}

/**
 * Evaluate a MULTIPLE_CHOICE answer
 */
function evaluateMultipleChoice(
  selectedIndex: number,
  correctIndex: number,
  correctAnswer: string
): EvaluationResult {
  const isCorrect = selectedIndex === correctIndex;

  return {
    isCorrect,
    similarityScore: isCorrect ? 1 : 0,
    correctAnswer,
    normalizedUserAnswer: String(selectedIndex),
    feedback: isCorrect ? 'Správně!' : `Špatně. Správná odpověď: ${correctAnswer}`,
    typoDetected: false,
  };
}

/**
 * Evaluate a MATCH_PAIRS answer
 */
function evaluateMatchPairs(
  userPairs: Record<string, string>,
  correctPairs: Record<string, string>,
  correctAnswer: string
): EvaluationResult {
  const totalPairs = Object.keys(correctPairs).length;
  let correctCount = 0;

  for (const [leftId, rightId] of Object.entries(userPairs)) {
    if (correctPairs[leftId] === rightId) {
      correctCount++;
    }
  }

  const score = correctCount / totalPairs;
  const isCorrect = score === 1;

  return {
    isCorrect,
    similarityScore: score,
    correctAnswer,
    normalizedUserAnswer: JSON.stringify(userPairs),
    feedback: isCorrect
      ? 'Správně!'
      : `${correctCount} z ${totalPairs} párů správně`,
    typoDetected: false,
  };
}

/**
 * Evaluate a WORD_BUILD answer
 */
function evaluateWordBuild(
  builtWord: string,
  targetWord: string
): EvaluationResult {
  const normalizedBuilt = normalizeAnswer(builtWord);
  const normalizedTarget = normalizeAnswer(targetWord);
  const isCorrect = normalizedBuilt === normalizedTarget;

  return {
    isCorrect,
    similarityScore: isCorrect ? 1 : calculateSimilarity(normalizedBuilt, normalizedTarget),
    correctAnswer: targetWord,
    normalizedUserAnswer: normalizedBuilt,
    feedback: isCorrect ? 'Správně!' : `Špatně. Správná odpověď: ${targetWord}`,
    typoDetected: false,
  };
}

/**
 * Main evaluation function - evaluates any exercise type
 */
export function evaluateAnswer(
  exercise: ExerciseItem,
  userAnswer: UserAnswer,
  typoTolerance: number = 0.2
): EvaluationResult {
  switch (userAnswer.type) {
    case 'TYPE_IN':
      return evaluateTypeIn(userAnswer.text, exercise.correctAnswer, typoTolerance);

    case 'MULTIPLE_CHOICE':
      if (exercise.modeData.type !== 'MULTIPLE_CHOICE') {
        throw new Error('Mode data mismatch');
      }
      return evaluateMultipleChoice(
        userAnswer.selectedIndex,
        exercise.modeData.correctIndex,
        exercise.correctAnswer
      );

    case 'MATCH_PAIRS':
      if (exercise.modeData.type !== 'MATCH_PAIRS') {
        throw new Error('Mode data mismatch');
      }
      return evaluateMatchPairs(
        userAnswer.pairs,
        exercise.modeData.correctPairs,
        exercise.correctAnswer
      );

    case 'LISTEN_TYPE':
      return evaluateTypeIn(userAnswer.text, exercise.correctAnswer, typoTolerance);

    case 'CLOZE_SMART':
      return evaluateTypeIn(userAnswer.text, exercise.correctAnswer, typoTolerance);

    case 'WORD_BUILD':
      if (exercise.modeData.type !== 'WORD_BUILD') {
        throw new Error('Mode data mismatch');
      }
      return evaluateWordBuild(userAnswer.builtWord, exercise.modeData.targetWord);

    default:
      throw new Error(`Unknown answer type: ${(userAnswer as UserAnswer).type}`);
  }
}

/**
 * Get feedback message based on evaluation result
 */
export function getFeedbackMessage(
  result: EvaluationResult,
  mode: ExerciseMode
): string {
  if (result.isCorrect) {
    if (result.typoDetected) {
      return `✓ ${result.feedback}`;
    }
    return '✓ Správně!';
  }

  return `✗ ${result.feedback}`;
}
